// ./src/components/features/Contacts/ContactTagFilter/ContactTagFilter.tsx
//
// Compact, single-row tag filter for the contacts filter bar. Shows the first few
// selected tags as pills + a "+N" overflow chip (native title lists the hidden
// ones on hover); clicking opens a searchable checklist popover to toggle tags.
// Keeps the filter bar to one row no matter how many tags are picked — unlike the
// editor's TagsSelectorInput, which grows a pill row per tag.
//
// ⚠ ON A PHONE the checklist is the full-screen picker (OptionPickerModal, multiple), as every selector is (owner,
//   2026-10-09) — the filters sit in a bottom-sheet drawer there.

import React, { useMemo, useState } from "react";
import {
  Input,
  Popover,
  PillsInput,
  Pill,
  Text,
  Group,
  Box,
  Stack,
  TextInput,
  ScrollArea,
  Checkbox,
  UnstyledButton,
  Button,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { getMostContrastingColor } from "@/utils/colorHelpers";
import { Tag } from "@/utils/api/TagService";
import { useTagsContext } from "@/contexts/TagsContext";
import { AppIcon, OptionPickerModal, usesPhonePicker } from "@/components/generic";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

export interface ContactTagFilterProps {
  value: string[];
  onChange: (tagIds: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  /** How many pills to show before collapsing the rest into a "+N" chip. */
  maxVisible?: number;
}

const ReadonlyPill: React.FC<{ tag: Tag }> = ({ tag }) => {
  const color = tag.color || "#6B7280";

  return (
    <Pill styles={{ root: { backgroundColor: color, color: getMostContrastingColor(color) } }}>
      {tag.name}
    </Pill>
  );
};

export const ContactTagFilter: React.FC<ContactTagFilterProps> = ({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  size = "sm",
  maxVisible = 2,
}) => {
  const { t } = useTranslation(["contacts", "tags", "common"]);
  const { activeTags, getTagById } = useTagsContext();

  const { isMobile } = useAppEnvironment();
  const phonePicker = usesPhonePicker({ isMobile, disabled });
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");

  const selectedTags = useMemo(
    () => value.map((id) => getTagById(id)).filter((tag): tag is Tag => tag !== undefined),
    [value, getTagById]
  );

  const visible = selectedTags.slice(0, maxVisible);
  const hidden = selectedTags.slice(maxVisible);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();

    if (!q) {
      return activeTags;
    }

    return activeTags.filter((tag) => tag.name?.toLowerCase().includes(q));
  }, [activeTags, search]);

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  const resolvedPlaceholder =
    placeholder ?? t("contacts:filters.tags.placeholder", { defaultValue: "Any tag" });

  const trigger = (
    <PillsInput
      onClick={() => !disabled && setOpened((o) => !o)}
      disabled={disabled}
      size={size}
      pointer
      mt={4}
      rightSection={<AppIcon iconName="ChevronDown" size={14} />}
      styles={{ input: { cursor: disabled ? "not-allowed" : "pointer" } }}
    >
      <Pill.Group>
        {selectedTags.length === 0 ? (
          <Text size="sm" c="dimmed" py={2}>
            {resolvedPlaceholder}
          </Text>
        ) : (
          <>
            {visible.map((tag) => (
              <ReadonlyPill key={tag.id} tag={tag} />
            ))}
            {hidden.length > 0 && (
              <Pill title={hidden.map((tag) => tag.name).join(", ")}>+{hidden.length}</Pill>
            )}
          </>
        )}
      </Pill.Group>
    </PillsInput>
  );

  if (phonePicker) {
    return (
      <Input.Wrapper label={label} size={size}>
        {trigger}
        <OptionPickerModal
          opened={opened}
          onClose={() => setOpened(false)}
          title={label ?? t("contacts:filters.tags.label", { defaultValue: "Tags" })}
          searchPlaceholder={t("contacts:filters.tags.search", { defaultValue: "Search tags" })}
          data={activeTags.filter((tag) => !!tag.id).map((tag) => ({ value: tag.id as string, label: tag.name ?? "" }))}
          multiple
          values={value}
          onToggle={toggle}
          renderOption={({ option }) => (
            <Group gap="xs" wrap="nowrap">
              <Box style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: getTagById(option.value)?.color || "#6B7280", flexShrink: 0 }} />
              <Text size="md" lineClamp={1}>
                {option.label}
              </Text>
            </Group>
          )}
          nothingFoundMessage={t("tags:selector.noTags", { defaultValue: "No tags available" })}
          // ⚠ Always there, disabled while nothing is picked — appearing on the first tick moved the rows under the finger.
          header={
            <Button variant="subtle" color="gray" size="md" mt="xs" disabled={value.length === 0} leftSection={<AppIcon iconName="X" size={14} />} onClick={() => onChange([])}>
              {t("contacts:filters.tags.clear", { defaultValue: "Clear tags" })}
            </Button>
          }
          doneLabel={t("common:buttons.done", { defaultValue: "Done" })}
        />
      </Input.Wrapper>
    );
  }

  return (
    <Input.Wrapper label={label} size={size}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        width="target"
        shadow="md"
        trapFocus
        withinPortal
        disabled={disabled}
      >
        <Popover.Target>{trigger}</Popover.Target>

        <Popover.Dropdown p="xs">
          <Stack gap="xs">
            <TextInput
              placeholder={t("contacts:filters.tags.search", { defaultValue: "Search tags" })}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<AppIcon iconName="Search" size={14} />}
              size="md"
              data-autofocus
            />
            <ScrollArea.Autosize mah={240} type="auto">
              {filtered.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="sm">
                  {t("tags:selector.noTags", { defaultValue: "No tags available" })}
                </Text>
              ) : (
                <Stack gap={2}>
                  {filtered.map((tag) => {
                    if (!tag.id) {
                      return null;
                    }

                    const checked = value.includes(tag.id);

                    return (
                      <UnstyledButton
                        key={tag.id}
                        onClick={() => toggle(tag.id!)}
                        p="xs"
                        style={{ borderRadius: "var(--mantine-radius-sm)" }}
                      >
                        <Group gap="xs" wrap="nowrap">
                          <Checkbox checked={checked} readOnly size="md" tabIndex={-1} />
                          <Box
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: tag.color || "#6B7280",
                              flexShrink: 0,
                            }}
                          />
                          <Text size="md" lineClamp={1}>
                            {tag.name}
                          </Text>
                        </Group>
                      </UnstyledButton>
                    );
                  })}
                </Stack>
              )}
            </ScrollArea.Autosize>
            {value.length > 0 && (
              <Button
                variant="subtle"
                color="gray"
                size="md"
                leftSection={<AppIcon iconName="X" size={14} />}
                onClick={() => onChange([])}
              >
                {t("contacts:filters.tags.clear", { defaultValue: "Clear tags" })}
              </Button>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Input.Wrapper>
  );
};
